import {
  AreaId,
  getDefaultConfiguration,
  resolveSashEdgeWidthsMM,
  SashbarConfiguration,
  WindowSpec,
} from '@cutr/constants/windows/windows';
import {
  CONFIGURATIONS_OPTIONS,
  SashType,
  SystemClass,
} from '@cutr/constants/windows/windows-configurations.ts';
import {
  Area,
  AreaGlassShape,
  getAreaGlassShapes,
  SideWidths,
} from '@cutr/constants/windows/windows-geometry.ts';
import Konva from 'konva';
import React, { useMemo } from 'react';
import { Arrow, Circle, Group, Line, Path, Rect, Text } from 'react-konva';

import { buildArcTopPath, makeArcTopClipFunc } from './arcTopPath';
import { position } from './colors';
import {
  DEFAULT_FRAME_BEAM_WIDTHS,
  FLOATING_MULLION_WIDTH_MM,
  MULLION_WIDTH_MM,
  SASH_MARGIN_MM,
  TRANSOM_WIDTH_MM,
} from './dimensions';
import { useKonva } from './KonvaContext';
import { Box } from './types';
import { useWindowEditor } from './WindowEditorContext';

type AreasProps = {
  x: number;
  y: number;

  windowSpec: Pick<WindowSpec, 'frameShape' | 'grid'>;
  areas: Area[];
  areaConfigurations: WindowSpec['areaConfigurations'];
  frameBeamWidthsMM?: SideWidths;
  systemClass?: SystemClass;
  selectedAreaId?: AreaId;
  onAreaSelect?: (id: AreaId) => void;
};

export const Areas = ({
  x,
  y,
  windowSpec,
  areas,
  areaConfigurations,
  frameBeamWidthsMM = DEFAULT_FRAME_BEAM_WIDTHS,
  systemClass,
  selectedAreaId,
  onAreaSelect,
}: AreasProps) => {
  const glassRectsById = useMemo(() => {
    const rects = getAreaGlassShapes(windowSpec, {
      frameBeamWidths: frameBeamWidthsMM,
      mullionWidthMM: MULLION_WIDTH_MM,
      floatingMullionWidthMM: FLOATING_MULLION_WIDTH_MM,
      transomWidthMM: TRANSOM_WIDTH_MM,
    });
    return Object.fromEntries(rects.map((r) => [r.id, r])) as Record<
      AreaId,
      AreaGlassShape
    >;
  }, [windowSpec, frameBeamWidthsMM]);

  return (
    <Group x={x} y={y}>
      {areas.map((area) => (
        <React.Fragment key={area.id}>
          <Sash
            area={area}
            glassRect={glassRectsById[area.id]}
            configuration={areaConfigurations[area.id]}
            systemClass={systemClass}
          />
          {onAreaSelect && (
            <AreaSelector
              area={area}
              glassRect={glassRectsById[area.id]}
              sashType={areaConfigurations[area.id]?.sashType}
              onAreaSelect={onAreaSelect}
              isSelected={selectedAreaId === area.id}
            />
          )}
        </React.Fragment>
      ))}
    </Group>
  );
};

const AreaSelector = ({
  area,
  glassRect,
  sashType,
  onAreaSelect,
  isSelected,
}: {
  area: Area;
  glassRect: AreaGlassShape;
  sashType: SashType | undefined;
  onAreaSelect: (id: AreaId) => void;
  isSelected: boolean;
}) => {
  const { readonly } = useWindowEditor();

  if (readonly) {
    return null;
  }

  // For sashType 'none' there is no sash drawn, so the selector should sit on
  // the visible glass; otherwise it sits at the centre of the area (which also
  // centres it on the sash).
  const isNone = sashType === 'none';
  const groupX = isNone ? glassRect.x : area.x;
  const groupY = isNone ? glassRect.y : area.y;
  const localWidth = isNone ? glassRect.widthMM : area.widthMM;
  const localHeight = isNone ? glassRect.heightMM : area.heightMM;

  const onMouseEnter = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();

    if (stage && stage.container()) {
      stage.container().style.cursor = 'pointer';
    }
  };
  const onMouseLeave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    if (stage && stage.container()) {
      stage.container().style.cursor = 'default';
    }
  };

  return (
    <Group
      x={groupX}
      y={groupY}
      onClick={() => onAreaSelect(area.id)}
      onTouchEnd={() => onAreaSelect(area.id)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Circle
        x={localWidth / 2}
        y={localHeight / 2}
        radius={80}
        stroke={position.areaSelectorStroke}
        strokeWidth={isSelected ? 8 : 3}
        fill={position.frameFill}
      />
      <Path
        x={localWidth / 2 + 42} // these numbers look nice
        y={localHeight / 2 - 34}
        data="M9.35437 5.6593L1.81531 13.2374V13.9796H2.5575L10.1356 6.44055L9.35437 5.6593ZM12.3622 0.659302C12.5575 0.659302 12.7919 0.737427 12.9481 0.893677L14.9012 2.8468C15.2137 3.1593 15.2137 3.70618 14.9012 4.01868L13.3778 5.54211L10.2528 2.41711L11.7762 0.893677C11.9325 0.737427 12.1278 0.659302 12.3622 0.659302ZM9.35437 3.31555L12.4794 6.44055L3.26062 15.6593H0.13562V12.5343L9.35437 3.31555Z"
        fill={position.frameStroke}
        scale={{
          x: 5,
          y: 5,
        }}
        rotation={90}
      />
    </Group>
  );
};

type SashProps = {
  area: Area;
  glassRect: AreaGlassShape;
  configuration: WindowSpec['areaConfigurations'][AreaId];
  systemClass?: SystemClass;
};

const Sash = ({ area, glassRect, configuration, systemClass }: SashProps) => {
  const { y, widthMM, heightMM } = area;
  const hasFloatingMullion = Boolean(area.floatingMullion);

  const { hasHandle, side } = area.floatingMullion ?? {};

  const sashBeam = resolveSashEdgeWidthsMM({
    sashBeamWidth: configuration?.sashBeamWidth,
    fallbackMM: Number(
      getDefaultConfiguration(
        'sashBeamWidth',
        CONFIGURATIONS_OPTIONS.system.options.find(
          (option) => option.value === systemClass
        )
      ).top.value
    ),
    systemClass,
    floatingMullionSide: side,
    isArched: Boolean(area.topEdge),
  });

  const marginLeftMM = side === 'left' || side === 'both' ? 0 : SASH_MARGIN_MM;
  const marginRightMM =
    side === 'right' || side === 'both' ? 0 : SASH_MARGIN_MM;
  const x = area.x + marginLeftMM;
  const sashWidthMM = widthMM - marginLeftMM - marginRightMM;
  const xInner = sashBeam.left;
  const innerWidthMM = sashWidthMM - sashBeam.left - sashBeam.right;

  const sashHeightMM = heightMM - SASH_MARGIN_MM * 2;
  const innerHeightMM = sashHeightMM - sashBeam.top - sashBeam.bottom;

  const { sashType, sashbars } = configuration!;

  if (sashType === 'none') {
    const glassBox = {
      x: glassRect.x,
      y: glassRect.y,
      width: glassRect.widthMM,
      height: glassRect.heightMM,
    };
    const overlay = <SashbarOverlay sashbars={sashbars} box={glassBox} />;

    if (glassRect.topEdge) {
      return (
        <Group
          clipFunc={makeArcTopClipFunc({
            box: glassBox,
            topEdge: glassRect.topEdge,
          })}
        >
          {overlay}
        </Group>
      );
    }

    return overlay;
  }

  const getHandleHeightMM = () => {
    if (sashHeightMM < 636) return 198;
    if (sashHeightMM < 836) return 318;
    if (sashHeightMM < 1036) return 418;
    if (sashHeightMM < 1236) return 518;
    if (sashHeightMM < 1436) return 618;
    if (sashHeightMM < 1836) return 718;
    if (sashHeightMM < 3036) return 1018;

    return sashHeightMM / 2;
  };
  const handleHeightMM = getHandleHeightMM();

  const localTopEdge = area.topEdge && {
    cx: area.topEdge.cx - x,
    cy: area.topEdge.cy - y - SASH_MARGIN_MM,
    r: area.topEdge.r,
  };

  const sashOuter = localTopEdge ? (
    <Path
      data={buildArcTopPath({
        xLeft: 0,
        xRight: sashWidthMM,
        yBottom: 0,
        yTop: sashHeightMM,
        topEdge: localTopEdge,
        arcInset: SASH_MARGIN_MM,
      })}
      stroke={position.frameStroke}
      fill={position.frameFill}
    />
  ) : (
    <Rect
      x={0}
      y={0}
      width={sashWidthMM}
      height={sashHeightMM}
      stroke={position.frameStroke}
      fill={position.frameFill}
    />
  );

  const innerGlass = localTopEdge ? (
    <Path
      data={buildArcTopPath({
        xLeft: xInner,
        xRight: xInner + innerWidthMM,
        yBottom: sashBeam.bottom,
        yTop: sashBeam.bottom + innerHeightMM,
        topEdge: localTopEdge,
        arcInset: SASH_MARGIN_MM + sashBeam.top,
      })}
      stroke={position.frameStroke}
      fill={position.glass}
    />
  ) : (
    <Rect
      x={xInner}
      y={sashBeam.bottom}
      width={innerWidthMM}
      height={innerHeightMM}
      stroke={position.frameStroke}
      fill={position.glass}
    />
  );

  const innerBox = {
    x: xInner,
    y: sashBeam.bottom,
    width: innerWidthMM,
    height: innerHeightMM,
  };
  const innerContents = (
    <>
      <SashInner box={innerBox} sashType={sashType} />
      <SashbarOverlay sashbars={sashbars} box={innerBox} />
    </>
  );
  const clippedInnerContents = localTopEdge ? (
    <Group
      clipFunc={makeArcTopClipFunc({
        box: innerBox,
        topEdge: {
          cx: localTopEdge.cx,
          cy: localTopEdge.cy,
          r: localTopEdge.r - SASH_MARGIN_MM - sashBeam.top,
        },
      })}
    >
      {innerContents}
    </Group>
  ) : (
    innerContents
  );

  return (
    <Group x={x} y={y + SASH_MARGIN_MM}>
      {sashOuter}
      {innerGlass}
      {clippedInnerContents}
      {(!hasFloatingMullion || hasHandle) && (
        <Handle
          box={{
            x: 0,
            y: 0,
            width: sashWidthMM,
            height: sashHeightMM,
          }}
          sashType={sashType}
          handleHeightMM={handleHeightMM}
          leftBeamMM={sashBeam.left}
          rightBeamMM={sashBeam.right}
          topBeamMM={sashBeam.top}
          bottomBeamMM={sashBeam.bottom}
        />
      )}
    </Group>
  );
};

type SashInnerProps = {
  box: Box;
  sashType: SashType;
};

const SashInner = ({ box, sashType }: SashInnerProps) => {
  const { x, y } = box;

  return (
    <Group x={x} y={y}>
      <SashTypeIndicator sashType={sashType} box={box} />
    </Group>
  );
};

type SashTypeIndicatorProps = {
  sashType: SashType;
  box: Box;
};

/**
 * ( )------( )-----( )
 *  |               |
 *  |   G   F    E  |
 *  |               |
 * ( )  H        D ( )
 *  |               |
 *  |   A   B    C  |
 *  |               |
 * ( )-----( )-----( )
 */
const SashTypeIndicator = ({ sashType, box }: SashTypeIndicatorProps) => {
  const { renderStyles } = useKonva();
  const { width, height } = box;
  const ARROW_POINTER_SIZE = 42 * renderStyles.sashTypeIndicatorArrowScale;
  const ARROW_POINTER_MARGIN = 32;
  const dash: [number, number] = [
    renderStyles.sashTypeIndicatorStrokeWidthMM * 8,
    renderStyles.sashTypeIndicatorStrokeWidthMM * 4,
  ];

  const A = [0, 0] as const;
  const B = [width / 2, 0] as const;
  const C = [width, 0] as const;
  const D = [width, height / 2] as const;
  const E = [width, height] as const;
  const F = [width / 2, height] as const;
  const G = [0, height] as const;
  const H = [0, height / 2] as const;

  if (sashType === 'none') {
    return null;
  }

  if (sashType === 'fixed') {
    return (
      <>
        <Line
          points={[A, E].flat()}
          stroke={position.sashTypeIndicatorStroke}
          strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
          dash={dash}
        />
        <Line
          points={[C, G].flat()}
          stroke={position.sashTypeIndicatorStroke}
          strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
          dash={dash}
        />
      </>
    );
  }

  if (sashType === 'turn-left') {
    return (
      <Line
        points={[A, D, G].flat()}
        stroke={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'turn-right') {
    return (
      <Line
        points={[C, H, E].flat()}
        stroke={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'tilt-up') {
    return (
      <Line
        points={[E, B, G].flat()}
        stroke={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'tilt-down') {
    return (
      <Line
        points={[A, F, C].flat()}
        stroke={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'turn-tilt-left') {
    return (
      <Line
        points={[C, F, A, D, G].flat()}
        stroke={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'turn-tilt-right') {
    return (
      <Line
        points={[A, F, C, H, E].flat()}
        stroke={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'slide-left') {
    const segment = [D, H].flat();
    segment[0] = segment[0] - ARROW_POINTER_MARGIN;
    segment[2] = segment[2] + ARROW_POINTER_MARGIN;

    return (
      <Arrow
        points={segment}
        stroke={position.sashTypeIndicatorStroke}
        pointerLength={ARROW_POINTER_SIZE}
        pointerWidth={ARROW_POINTER_SIZE}
        fill={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'slide-right') {
    const segment = [H, D].flat();
    segment[0] = segment[0] + ARROW_POINTER_MARGIN;
    segment[2] = segment[2] - ARROW_POINTER_MARGIN;

    return (
      <Arrow
        points={segment}
        stroke={position.sashTypeIndicatorStroke}
        pointerLength={ARROW_POINTER_SIZE}
        pointerWidth={ARROW_POINTER_SIZE}
        fill={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'slide-up') {
    const segment = [B, F].flat();
    segment[1] = segment[1] + ARROW_POINTER_MARGIN;
    segment[3] = segment[3] - ARROW_POINTER_MARGIN;

    return (
      <Arrow
        points={segment}
        stroke={position.sashTypeIndicatorStroke}
        pointerLength={ARROW_POINTER_SIZE}
        pointerWidth={ARROW_POINTER_SIZE}
        fill={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  if (sashType === 'slide-down') {
    const segment = [F, B].flat();
    segment[1] = segment[1] - ARROW_POINTER_MARGIN;
    segment[3] = segment[3] + ARROW_POINTER_MARGIN;

    return (
      <Arrow
        points={segment}
        stroke={position.sashTypeIndicatorStroke}
        pointerLength={ARROW_POINTER_SIZE}
        pointerWidth={ARROW_POINTER_SIZE}
        fill={position.sashTypeIndicatorStroke}
        strokeWidth={renderStyles.sashTypeIndicatorStrokeWidthMM}
        dash={dash}
      />
    );
  }

  return (
    <>
      <LabeledPoint point={A} text="A" />
      <LabeledPoint point={B} text="B" />
      <LabeledPoint point={C} text="C" />
      <LabeledPoint point={D} text="D" />
      <LabeledPoint point={E} text="E" />
      <LabeledPoint point={F} text="F" />
      <LabeledPoint point={G} text="G" />
      <LabeledPoint point={H} text="H" />
    </>
  );
};

type HandleProps = {
  box: Box;
  sashType: SashType;
  handleHeightMM: number;
  leftBeamMM: number;
  rightBeamMM: number;
  topBeamMM: number;
  bottomBeamMM: number;
};

/**
 * ┌───────────────────┐
 * │       ( ) C       │
 * │     ┌───────┐     │
 * │     │       │     │
 * │ ( )D│       │ ( )B│
 * │     │       │     │
 * │     └───────┘     │
 * │       ( ) A       │
 * └───────────────────┘
 */
const Handle = ({
  box,
  sashType,
  handleHeightMM,
  leftBeamMM,
  rightBeamMM,
  topBeamMM,
  bottomBeamMM,
}: HandleProps) => {
  const { width, height } = box;

  const D = [leftBeamMM / 2, handleHeightMM] as const;
  const A = [width / 2, bottomBeamMM / 2] as const;
  const B = [width - rightBeamMM / 2, handleHeightMM] as const;
  const C = [width / 2, height - topBeamMM / 2] as const;

  const sashTypeToHandle: Record<SashType, HandleInnerProps | null> = {
    none: null,
    fixed: null,
    'turn-left': {
      point: B,
      direction: 'vertical',
    },
    'turn-right': {
      point: D,
      direction: 'vertical',
    },
    'tilt-up': {
      point: A,
      direction: 'horizontal',
    },
    'tilt-down': {
      point: C,
      direction: 'horizontal',
    },
    'turn-tilt-left': {
      point: B,
      direction: 'vertical',
    },
    'turn-tilt-right': {
      point: D,
      direction: 'vertical',
    },
  };
  const handle = sashTypeToHandle[sashType];

  if (!handle) {
    return null;
  }

  return <HandleInner point={handle.point} direction={handle.direction} />;
};

type HandleInnerProps = {
  point: readonly [number, number];
  direction: 'vertical' | 'horizontal';
};

const HandleInner = ({ point, direction }: HandleInnerProps) => {
  const [x, y] = point;
  const scaleFactor = 2;
  const HANDLE_BASE_HEIGHT_MM = 60 * scaleFactor;
  const HANDLE_BASE_WIDTH_MM = 18 * scaleFactor;
  const HANDLE_GRIP_HEIGHT_MM = 60 * scaleFactor;
  const HANDLE_GRIP_WIDTH_MM = 8 * scaleFactor;

  return (
    <Group x={x} y={y} rotation={direction === 'horizontal' ? 270 : 0}>
      <Rect
        x={-HANDLE_BASE_WIDTH_MM / 2}
        y={-HANDLE_BASE_HEIGHT_MM / 2}
        width={HANDLE_BASE_WIDTH_MM}
        height={HANDLE_BASE_HEIGHT_MM}
        fill={position.handleFill}
        stroke={position.handleStroke}
        strokeWidth={3}
      />
      <Rect
        x={-HANDLE_GRIP_WIDTH_MM / 2}
        y={-HANDLE_GRIP_HEIGHT_MM / 2 - HANDLE_BASE_HEIGHT_MM / 2}
        width={HANDLE_GRIP_WIDTH_MM}
        height={HANDLE_GRIP_HEIGHT_MM}
        fill={position.handleFill}
        stroke={position.handleStroke}
        strokeWidth={3}
      />
    </Group>
  );
};

type LabeledPointProps = {
  point: readonly [number, number];
  text: string;
};

type SashbarOverlayProps = {
  sashbars: SashbarConfiguration[] | undefined;
  box: Box;
};

const SashbarOverlay = ({ sashbars, box }: SashbarOverlayProps) => {
  const sashbar = sashbars?.[0];

  const gridId = sashbar?.layout.value;
  const profileWidthMm = getSashbarProfileWidthMm(sashbar);
  const enabled = Boolean(sashbar);

  const { x, y, width, height } = box;

  const segments = useMemo(() => {
    if (!enabled || !gridId) return [];
    return computeSashbarSegments(gridId, { x: 0, y: 0, width, height });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, gridId, profileWidthMm, enabled]);

  if (segments.length === 0) {
    return null;
  }

  return (
    <Group x={x} y={y}>
      {segments.map((points, index) => (
        <Line
          key={index}
          points={points}
          strokeWidth={profileWidthMm}
          stroke={position.sashbar}
        />
      ))}
    </Group>
  );
};

const getSashbarProfileWidthMm = (
  sashbar: SashbarConfiguration | undefined
): number => {
  const match = sashbar?.profile.value.match(/(\d+)mm/);
  return match ? Number(match[1]) : 18;
};

const computeSashbarSegments = (gridId: string, box: Box): number[][] => {
  const { width, height } = box;
  const midX = width / 2;
  const midY = height / 2;

  if (gridId === '1-horizontal-1-vertical-down') {
    return [
      [0, midY, width, midY],
      [midX, 0, midX, midY],
    ];
  }
  if (gridId === '1-horizontal-1-vertical-up') {
    return [
      [0, midY, width, midY],
      [midX, midY, midX, height],
    ];
  }
  if (gridId === '1-vertical-1-horizontal-right') {
    return [
      [midX, 0, midX, height],
      [midX, midY, width, midY],
    ];
  }
  if (gridId === '1-vertical-1-horizontal-left') {
    return [
      [midX, 0, midX, height],
      [0, midY, midX, midY],
    ];
  }

  const verticalMatch = gridId.match(/(\d+)-vertical/);
  const horizontalMatch = gridId.match(/(\d+)-horizontal/);
  const verticalCount = verticalMatch ? Number(verticalMatch[1]) : 0;
  const horizontalCount = horizontalMatch ? Number(horizontalMatch[1]) : 0;

  const segments: number[][] = [];
  for (let i = 1; i <= verticalCount; i++) {
    const lineX = (width / (verticalCount + 1)) * i;
    segments.push([lineX, 0, lineX, height]);
  }
  for (let i = 1; i <= horizontalCount; i++) {
    const lineY = (height / (horizontalCount + 1)) * i;
    segments.push([0, lineY, width, lineY]);
  }
  return segments;
};

const LabeledPoint = ({ point, text }: LabeledPointProps) => {
  const [x, y] = point;

  const TEXT_OFFSET = [20, 50];

  return (
    <>
      <Circle x={x} y={y} radius={10} fill="red" />
      <Text
        x={x + TEXT_OFFSET[0]}
        y={y + TEXT_OFFSET[1]}
        text={text}
        stroke="red"
        fontSize={48}
        scaleY={-1}
      />
    </>
  );
};
